import { Link } from 'react-router';
import { ArrowRight, Building2, Database, LineChart, Settings2, UserCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { EnterpriseSalesNav } from '../components/enterprise/EnterpriseSalesNav';
import { useSEO } from '../lib/seo';

const flow = [
  {
    icon: Building2,
    title: '1) Enterprise setup (admin)',
    detail:
      'Create or load an organization in Enterprise Command Center, configure tenant settings, seat policy, and governance values.',
    where: '/enterprise/admin',
  },
  {
    icon: UserCheck,
    title: '2) Seat assignment (admin)',
    detail:
      'Assign users to the organization using membership controls (single or bulk provisioning). Only active members are counted in enterprise analytics.',
    where: '/enterprise/admin',
  },
  {
    icon: Settings2,
    title: '3) Data entry (consumer users)',
    detail:
      'Enterprise users submit check-ins in the standalone enterprise workspace. Check-ins are persisted server-side with organization context while using the shared RTI engine.',
    where: '/work/check-in (preferred) or /daily-check-in (legacy bridge)',
  },
  {
    icon: LineChart,
    title: '4) Monitoring (enterprise)',
    detail:
      'Command Center and enterprise wave surfaces aggregate check-ins + memberships into active seats, check-ins (30d), cohort mix, trend delta, and risk/forecast signals.',
    where: '/enterprise/admin',
  },
  {
    icon: Database,
    title: '5) Exports and operations',
    detail:
      'Operators can export board packs/readouts (CSV/PDF), run wave previews, and manage partner workflow settings for buyer-facing reporting.',
    where: '/enterprise/admin/wave-2',
  },
];

export default function EnterpriseHowItWorks() {
  useSEO({
    title: 'How Enterprise Data Flow Works | TENSION',
    description:
      'Understand exactly how TENSION Enterprise ingests data from users, how admins configure tenants, and how analytics and controls are generated.',
    canonical: '/enterprise/how-it-works',
    ogType: 'website',
  });

  return (
    <div className="enterprise-public-shell min-h-screen bg-[radial-gradient(circle_at_top,rgba(30,64,175,0.2),transparent_35%),linear-gradient(180deg,#020617_0%,#0f172a_50%,#020617_100%)]">
      <EnterpriseSalesNav variant="dark" />
      <div className="mx-auto max-w-6xl space-y-6 px-4 py-8">
        <header className="enterprise-hero-shell">
          <h1 className="text-3xl font-black tracking-tight text-slate-100 md:text-4xl">How the enterprise program actually functions</h1>
          <p className="mt-3 max-w-4xl text-sm leading-relaxed text-slate-300 md:text-base">
            Data enters from two places: <span className="font-semibold text-slate-100">enterprise admins</span> (tenant + seat governance)
            and <span className="font-semibold text-slate-100">consumer users</span> (check-ins). Enterprise analytics are then generated from
            organization memberships plus server-persisted check-ins.
          </p>
        </header>

        <section className="grid gap-4 md:grid-cols-2">
          {flow.map((step) => {
            const Icon = step.icon;
            return (
              <Card key={step.title} className="enterprise-surface-card p-5">
                <div className="inline-flex rounded-lg border border-slate-600 bg-slate-800/80 p-2">
                  <Icon className="h-5 w-5 text-blue-300" aria-hidden />
                </div>
                <h2 className="mt-3 text-lg font-semibold text-slate-100">{step.title}</h2>
                <p className="mt-2 text-sm text-slate-300">{step.detail}</p>
                <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Surface: {step.where}</p>
              </Card>
            );
          })}
        </section>

        <Card className="enterprise-surface-card p-6">
          <h2 className="text-xl font-semibold text-slate-100">Why you may not see data yet</h2>
          <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-slate-300">
            <li>No active enterprise organization is selected for the user context.</li>
            <li>Users are not added as active organization members.</li>
            <li>Users are not signed in when submitting check-ins (server persistence requires auth token).</li>
            <li>No recent check-ins exist in the 30-day window, so executive metrics remain sparse.</li>
          </ul>
        </Card>

        <div className="flex flex-wrap gap-3 pb-6">
          <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white" asChild>
            <Link to="/enterprise/start-here">
              Start setup wizard
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
          <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white" asChild>
            <Link to="/enterprise/live-demo">
              Open hands-on live demo
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
          <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100" asChild>
            <Link to="/enterprise/admin">Open Enterprise Command Center</Link>
          </Button>
          <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100" asChild>
            <Link to="/work/home">Open employee workspace</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

